import { v4 as uuid } from 'uuid';
import type { ActionItem, ActionItemStatus } from '@/types';
import { actionItemsStorage } from '@/services/storage';
import { nowIso } from '@/utils/dateUtils';

export async function listActionItems(): Promise<ActionItem[]> {
  return actionItemsStorage.getAll();
}

export async function listActionItemsForNewsletter(newsletterId: string): Promise<ActionItem[]> {
  const all = await actionItemsStorage.getAll();
  return all.filter((item) => item.newsletterId === newsletterId);
}

export interface CreateActionItemInput {
  newsletterId: string;
  title: string;
  owner: string;
  dueDate: string;
  status?: ActionItemStatus;
  notes?: string;
}

export async function createActionItem(input: CreateActionItemInput): Promise<ActionItem> {
  const timestamp = nowIso();
  const item: ActionItem = {
    id: uuid(),
    newsletterId: input.newsletterId,
    title: input.title,
    owner: input.owner,
    dueDate: input.dueDate,
    status: input.status ?? 'open',
    notes: input.notes,
    createdAt: timestamp,
    updatedAt: timestamp,
    completedAt: input.status === 'completed' ? timestamp : undefined,
  };
  await actionItemsStorage.save(item);
  return item;
}

export async function updateActionItem(item: ActionItem): Promise<ActionItem> {
  const timestamp = nowIso();
  const updated: ActionItem = {
    ...item,
    updatedAt: timestamp,
    completedAt: item.status === 'completed' ? item.completedAt ?? timestamp : undefined,
  };
  await actionItemsStorage.save(updated);
  return updated;
}

export async function setActionItemStatus(item: ActionItem, status: ActionItemStatus): Promise<ActionItem> {
  return updateActionItem({ ...item, status });
}

export async function deleteActionItem(id: string): Promise<void> {
  await actionItemsStorage.remove(id);
}

/**
 * Copies every incomplete action item from the previous newsletter into the new one,
 * preserving owner/due date/status and linking back via `carriedFromId` so the UI can
 * badge them as carried forward.
 */
export async function carryForwardIncompleteItems(
  fromNewsletterId: string,
  toNewsletterId: string,
): Promise<ActionItem[]> {
  const previousItems = await listActionItemsForNewsletter(fromNewsletterId);
  const incomplete = previousItems.filter((item) => item.status !== 'completed');
  const timestamp = nowIso();

  const carried: ActionItem[] = incomplete.map((item) => ({
    id: uuid(),
    newsletterId: toNewsletterId,
    title: item.title,
    owner: item.owner,
    dueDate: item.dueDate,
    status: item.status,
    notes: item.notes,
    carriedFromId: item.id,
    createdAt: timestamp,
    updatedAt: timestamp,
  }));

  if (carried.length > 0) {
    await actionItemsStorage.saveMany(carried);
  }
  return carried;
}
